/**
 * Search metadata registry.
 *
 * Maps nav-item IDs (from navConfig.ts) to descriptions and additional
 * keywords that improve fuzzy search hit-rate.  The search engine merges
 * this metadata with the canonical nav labels/icons at query time so we
 * never duplicate the navigation hierarchy itself.
 */

export type SearchMeta = {
  description: string;
  keywords: string[];
};

/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
export const searchMetadata: Record<string, SearchMeta> = {
  // ── Dashboard ──────────────────────────────────────────────────────
  "app-dashboard": {
    description: "Application control panel with package management and system metrics",
    keywords: ["apps", "packages", "software", "home", "main", "overview", "installed"]
  },
  "system-dashboard": {
    description: "System telemetry, health monitoring, bandwidth, CPU and memory charts",
    keywords: ["telemetry", "health", "bandwidth", "cpu", "memory", "disk", "monitoring", "server", "metrics", "performance"]
  },

  // Streaming Dashboard group (navigable group header)
  "streaming-dashboard": {
    description: "Media server streaming sessions and watch activity monitoring",
    keywords: ["emby", "jellyfin", "plex", "streaming", "watch", "sessions", "media", "movies", "tv", "shows", "wsd"]
  },
  "wsd-analytics": {
    description: "Watch analytics and streaming statistics",
    keywords: ["statistics", "charts", "watch", "history", "analytics", "movies", "shows"]
  },
  "wsd-logs": {
    description: "Streaming activity logs and event history",
    keywords: ["activity", "history", "events", "log"]
  },
  "wsd-settings": {
    description: "Streaming dashboard configuration and connection settings",
    keywords: ["configure", "connection", "emby", "jellyfin", "api", "key"]
  },
  "wsd-control-center": {
    description: "Central management hub for media server administration",
    keywords: ["control", "manage", "admin", "media", "server", "emby", "jellyfin"]
  },
  "cc-overview": {
    description: "Control Center overview with server status, users and API keys",
    keywords: ["overview", "status", "health", "users", "api", "keys", "summary"]
  },
  "cc-server-users": {
    description: "Media server user management and access control",
    keywords: ["users", "access", "permissions", "media", "server", "accounts"]
  },
  "cc-devices": {
    description: "Connected devices and client management",
    keywords: ["devices", "clients", "connected", "network", "streaming"]
  },
  "cc-media-portal": {
    description: "Immersive cinematic media browsing surface for movies, TV shows and music",
    keywords: ["media", "portal", "movies", "browse", "cinematic", "library", "films", "tv", "shows"]
  },

  // ── Settings ───────────────────────────────────────────────────────
  "settings-email": {
    description: "Email server configuration and template management",
    keywords: ["email", "smtp", "mail", "templates", "notifications"]
  },
  "settings-notifications": {
    description: "System notification preferences and delivery settings",
    keywords: ["notifications", "alerts", "delivery", "preferences"]
  },
  "settings-general": {
    description: "Core dashboard settings including instance, timezone, display and branding",
    keywords: ["general", "instance", "timezone", "locale", "display", "branding", "feature flags", "uploads"]
  },
  "gs-instance-settings": {
    description: "Instance name, URL and core configuration",
    keywords: ["instance", "name", "url", "hostname", "server", "admin email", "description", "web root"]
  },
  "gs-timezone-locale": {
    description: "Timezone and locale configuration",
    keywords: ["timezone", "locale", "language", "region", "date", "time"]
  },
  "gs-sessions-security": {
    description: "Session security and authentication settings — moved to Session Settings",
    keywords: ["session", "security", "auth", "login", "timeout", "remember me", "token", "cookie", "csrf"]
  },
  "gs-display-preferences": {
    description: "Dashboard display and visual preferences",
    keywords: ["display", "theme", "appearance", "visual", "layout", "density", "sidebar", "animations", "default page", "landing page"]
  },
  "gs-additional-dashboard-settings": {
    description: "Additional dashboard configuration including Redis, disk, network and bandwidth",
    keywords: ["dashboard", "additional", "extra", "config", "redis", "cache", "vnstat", "bandwidth", "disk", "mount", "network", "interface", "disk space"]
  },
  "gs-uploads": {
    description: "File upload size limits and configuration",
    keywords: ["uploads", "files", "size", "limit", "max", "avatar", "banner", "kilobytes", "upload"]
  },
  "gs-branding": {
    description: "Dashboard branding, logo and visual identity",
    keywords: ["branding", "logo", "brand", "identity", "favicon", "custom", "avatar", "banner", "collapsed logo", "full logo", "default avatar"]
  },
  "gs-auth-pages": {
    description: "Login and registration page customization",
    keywords: ["auth", "login", "registration", "pages", "custom", "welcome", "message", "markdown", "logo"]
  },
  "gs-feature-flags": {
    description: "Enable or disable dashboard features",
    keywords: [
      "features", "flags", "toggle", "enable", "disable", "wsd", "webconsole",
      "maintenance", "mode", "registration", "notification", "channels",
      "terminal", "wsdashboard", "streaming", "monitor", "playback", "mirror", "ttl"
    ]
  },
  "settings-sessions": {
    description: "Session timeout, persistence and concurrency settings",
    keywords: ["sessions", "timeout", "cookies", "concurrency", "persistence"]
  },
  "ss-timeouts": {
    description: "Session and idle timeout duration configuration",
    keywords: ["timeout", "idle", "duration", "expiry", "inactivity", "user timeout", "guest timeout", "minutes"]
  },
  "ss-persistence-cookies": {
    description: "Session persistence and cookie settings",
    keywords: ["cookies", "persistence", "remember", "storage", "remember me", "auto-extend", "activity", "expiration", "days"]
  },
  "ss-concurrency": {
    description: "Concurrent session limits per user",
    keywords: ["concurrency", "simultaneous", "limit", "multiple", "multiple sessions", "device", "revoke"]
  },
  "ss-session-operations": {
    description: "Bulk session management operations",
    keywords: ["operations", "bulk", "revoke", "clear", "manage", "invalidate", "non-admin", "force logout"]
  },
  "settings-security": {
    description: "Security enforcement, banned IPs, and network controls",
    keywords: ["security", "firewall", "ban", "block", "network", "enforcement", "geo"]
  },
  "sec-firewall-rules": {
    description: "View and manage iptables firewall rules and network filtering",
    keywords: ["firewall", "rules", "iptables", "filter", "network", "ports", "drop", "accept", "chain"]
  },
  "sec-geo-lock": {
    description: "Lock dashboard access to specific countries using GeoIP filtering",
    keywords: ["geo", "lock", "country", "geoip", "restrict", "location", "geographic", "whitelist"]
  },
  "sec-geo-block": {
    description: "Block dashboard access from specific countries using GeoIP filtering",
    keywords: ["geo", "block", "country", "geoip", "restrict", "location", "geographic", "blacklist", "deny"]
  },
  "sec-fail2ban": {
    description: "Manage Fail2Ban jails, allowlists, and intrusion prevention settings",
    keywords: ["fail2ban", "jail", "ban", "brute-force", "intrusion", "allowlist", "block", "ssh", "prevention", "security"]
  },
  "settings-registration": {
    description: "User registration policies, quotas and welcome email",
    keywords: ["registration", "signup", "invite", "quota", "policy", "password", "username", "welcome"]
  },
  "reg-activation": {
    description: "Registration activation mode and invitation settings",
    keywords: ["activation", "invite", "open", "closed", "approval", "email activation", "admin activation", "disabled"]
  },
  "reg-quotas": {
    description: "Disk quota and resource limits for new users",
    keywords: ["quotas", "disk", "space", "limit", "storage", "unlimited", "megabytes", "gigabytes", "terabytes"]
  },
  "reg-default-group": {
    description: "Default user group assigned to new registrations",
    keywords: ["default", "group", "registration", "assign", "access", "membership", "new user", "role"]
  },
  "reg-username-policy": {
    description: "Username format requirements and restrictions",
    keywords: ["username", "format", "rules", "requirements", "length", "minimum", "maximum", "lowercase", "regex", "pattern"]
  },
  "reg-password-policy": {
    description: "Password strength requirements and rules",
    keywords: ["password", "strength", "requirements", "rules", "complexity", "minimum", "maximum", "characters"]
  },
  "reg-welcome-email": {
    description: "Welcome email template sent to new users",
    keywords: ["welcome", "email", "template", "onboarding", "new user", "send", "notification"]
  },

  // ── User Management ────────────────────────────────────────────────
  "users-admin": {
    description: "Create, manage, ban and delete user accounts",
    keywords: ["users", "accounts", "create", "delete", "ban", "manage", "admin", "members"]
  },
  "users-groups": {
    description: "User group management and application access control",
    keywords: ["groups", "teams", "access", "permissions", "applications", "membership"]
  },
  "users-roles": {
    description: "Role definitions and permission assignment",
    keywords: ["roles", "permissions", "admin", "staff", "user", "access", "rbac"]
  },

  // ── System ─────────────────────────────────────────────────────────
  "system-db-management": {
    description: "Database management via phpMyAdmin",
    keywords: ["database", "mysql", "mariadb", "phpmyadmin", "sql", "tables"]
  },
  "system-console": {
    description: "Web-based terminal for server administration",
    keywords: ["terminal", "console", "shell", "bash", "command", "cli", "ssh"]
  },
  "system-api-settings": {
    description: "API key management and configuration",
    keywords: ["api", "keys", "tokens", "integration", "endpoints"]
  },
  "system-app-templates": {
    description: "Application definition templates and customization",
    keywords: ["templates", "apps", "definitions", "customize", "software"]
  },
  "system-ssl-control": {
    description: "SSL/TLS certificate management via Let's Encrypt",
    keywords: ["ssl", "tls", "certificates", "https", "letsencrypt", "acme", "dns"]
  },
  "system-vpn-control": {
    description: "WireGuard VPN peer configuration and management",
    keywords: ["vpn", "wireguard", "peers", "tunnel", "network", "remote"]
  },
  "system-news": {
    description: "News announcements and release notes from QuickBox",
    keywords: ["news", "announcements", "releases", "updates", "blog", "bulletin", "notices"]
  },
  "system-changelog": {
    description: "Version history, changelog and update center",
    keywords: ["changelog", "updates", "version", "release", "upgrade", "history"]
  },
  "system-logs": {
    description: "System logs, audit trail and application events",
    keywords: ["logs", "audit", "events", "errors", "debug", "syslog", "journal"]
  },
  "system-documentation": {
    description: "QuickBox Pro documentation and guides",
    keywords: ["docs", "documentation", "help", "guides", "manual", "reference"]
  },

  // ── Profile / Account ─────────────────────────────────────────────
  "profile-picture": {
    description: "Update your profile picture and avatar",
    keywords: ["avatar", "photo", "picture", "image", "profile"]
  },
  "profile-account": {
    description: "Account settings and personal information",
    keywords: ["account", "email", "username", "personal", "info"]
  },
  "profile-links": {
    description: "Quick navigation links on your profile",
    keywords: ["links", "shortcuts", "quick", "bookmarks"]
  },
  "profile-system-metrics": {
    description: "Personal disk usage and system resource metrics",
    keywords: ["disk", "usage", "storage", "metrics", "quota", "space"]
  },
  "security-passkeys": {
    description: "Manage passkeys for passwordless authentication",
    keywords: ["passkeys", "webauthn", "fido", "biometric", "passwordless"]
  },
  "security-2fa": {
    description: "Two-factor authentication setup and management",
    keywords: ["2fa", "totp", "authenticator", "mfa", "two-factor", "security"]
  },
  "security-sessions": {
    description: "View and manage your active login sessions",
    keywords: ["sessions", "devices", "active", "login", "revoke"]
  },
  "notification-settings-channels": {
    description: "Configure notification delivery channels",
    keywords: ["channels", "email", "push", "delivery"]
  },
  "notification-settings-channel-setup": {
    description: "Set up and test notification channels",
    keywords: ["setup", "test", "configure", "channels"]
  },
  "notification-settings-disk-usage": {
    description: "Disk usage alert thresholds and notifications",
    keywords: ["disk", "usage", "alerts", "storage", "threshold"]
  },
  "notification-settings-events": {
    description: "Notification event preferences and subscriptions",
    keywords: ["events", "preferences", "subscribe", "notifications"]
  },
  "notification-settings-updates-digest": {
    description: "Consolidate pending update notifications into a scheduled summary email",
    keywords: ["updates", "digest", "schedule", "summary", "consolidate", "email", "pending", "notifications"]
  },
  "notification-settings-events-applications": {
    description: "Notification preferences for app health, lifecycle, and update alerts",
    keywords: ["applications", "services", "health", "offline", "online", "degraded", "disk", "update"]
  },
  "notification-settings-events-security": {
    description: "Notification preferences for login, session, password, and MFA changes",
    keywords: ["security", "login", "session", "password", "2fa", "mfa", "passkey", "revoked"]
  },
  "notification-settings-events-automation": {
    description: "Notification preferences for background tasks and scheduled jobs",
    keywords: ["automation", "jobs", "tasks", "background", "completed", "failed", "scheduled"]
  },
  "notification-settings-events-support": {
    description: "Notification preferences for support tickets and direct messages",
    keywords: ["support", "tickets", "chat", "messages", "collaboration", "mention"]
  },
  "notification-settings-events-digest": {
    description: "Notification preferences for daily and weekly digest emails and quiet hours",
    keywords: ["digest", "daily", "weekly", "quiet", "hours", "mute", "summary", "noise"]
  },
  "notification-center-all": {
    description: "View all your notifications",
    keywords: ["notifications", "inbox", "messages", "alerts"]
  },
  "support-access": {
    description: "Enable or disable support team access to your server",
    keywords: ["support", "access", "remote", "help", "troubleshoot"]
  },

  // ── Software ───────────────────────────────────────────────────────
  "media-management-bazarr": { description: "Subtitle management and downloading", keywords: ["subtitles", "srt", "sub"] },
  "media-management-lazylibrarian": { description: "Book and audiobook management", keywords: ["books", "audiobooks", "ebooks", "reading"] },
  "media-management-lidarr": { description: "Music collection management", keywords: ["music", "albums", "artists", "songs"] },
  "media-management-medusa": { description: "TV show management and automation", keywords: ["tv", "shows", "series", "episodes"] },
  "media-management-mylar3": { description: "Comic book management", keywords: ["comics", "manga", "issues"] },
  "media-management-radarr": { description: "Movie collection management and automation", keywords: ["movies", "films", "cinema"] },
  "media-management-readarr": { description: "Book and ebook management", keywords: ["books", "ebooks", "reading", "library"] },
  "media-management-sickchill": { description: "TV show management", keywords: ["tv", "shows", "series"] },
  "media-management-sickgear": { description: "TV show management and automation", keywords: ["tv", "shows", "series"] },
  "media-management-sonarr": { description: "TV series management and automation", keywords: ["tv", "shows", "series", "episodes", "anime"] },
  "media-servers-airsonic": { description: "Music streaming server", keywords: ["music", "streaming", "audio", "songs"] },
  "media-servers-emby": { description: "Media server for movies, TV and music", keywords: ["movies", "tv", "music", "streaming", "media", "server"] },
  "media-servers-jellyfin": { description: "Free and open-source media server", keywords: ["movies", "tv", "music", "streaming", "media", "server", "open source"] },
  "media-servers-plex": { description: "Media server for streaming movies, TV and music", keywords: ["movies", "tv", "music", "streaming", "media", "server"] },
  "media-servers-subsonic": { description: "Music streaming server", keywords: ["music", "streaming", "audio"] },
  "media-servers-tautulli": { description: "Plex monitoring and statistics", keywords: ["plex", "monitoring", "statistics", "analytics", "watch"] },
  "media-servers-xteve": { description: "IPTV proxy for Plex and Emby", keywords: ["iptv", "proxy", "live", "tv", "channels"] },
  "media-requests-jellyseerr": { description: "Media request management for Jellyfin", keywords: ["requests", "jellyfin", "movies", "tv"] },
  "media-requests-ombi": { description: "Media request and user management", keywords: ["requests", "movies", "tv", "users"] },
  "media-requests-overseerr": { description: "Media request management for Plex", keywords: ["requests", "plex", "movies", "tv"] },
  "media-requests-requestrr": { description: "Discord-based media requesting", keywords: ["requests", "discord", "movies", "tv"] },
  "media-requests-seerr": { description: "Media request management", keywords: ["requests", "movies", "tv"] },
  "download-clients-deluge": { description: "Lightweight BitTorrent client", keywords: ["torrent", "download", "bittorrent", "p2p"] },
  "download-clients-flood": { description: "Modern web UI for rTorrent", keywords: ["torrent", "download", "rtorrent", "ui"] },
  "download-clients-jdownloader": { description: "Direct download manager", keywords: ["download", "direct", "links", "ddl"] },
  "download-clients-nzbget": { description: "Usenet download client", keywords: ["usenet", "nzb", "download", "newsgroups"] },
  "download-clients-pyload": { description: "Download manager for file hosting sites", keywords: ["download", "hosting", "links", "manager"] },
  "download-clients-qbittorrent": { description: "Feature-rich BitTorrent client", keywords: ["torrent", "download", "bittorrent", "p2p", "qbt"] },
  "download-clients-rtorrent": { description: "Console-based BitTorrent client", keywords: ["torrent", "download", "bittorrent", "console"] },
  "download-clients-rutorrent": { description: "Web frontend for rTorrent", keywords: ["torrent", "download", "rtorrent", "web"] },
  "download-clients-sabnzbd": { description: "Usenet download client", keywords: ["usenet", "nzb", "download", "newsgroups"] },
  "download-clients-transmission": { description: "Lightweight BitTorrent client", keywords: ["torrent", "download", "bittorrent", "p2p"] },
  "indexers-autobrr": { description: "IRC and torrent automation", keywords: ["irc", "torrent", "automation", "announce"] },
  "indexers-autodl": { description: "IRC torrent auto-downloader", keywords: ["irc", "torrent", "auto", "download"] },
  "indexers-jackett": { description: "Torrent/NZB indexer proxy", keywords: ["indexer", "proxy", "torrent", "nzb", "search"] },
  "indexers-nzbhydra2": { description: "NZB meta search aggregator", keywords: ["nzb", "search", "usenet", "aggregator"] },
  "indexers-prowlarr": { description: "Indexer management and proxy", keywords: ["indexer", "proxy", "torrent", "nzb", "search"] },
  "automation-autoscan": { description: "Automated media library scanning", keywords: ["scan", "library", "media", "plex", "emby"] },
  "automation-filebot": { description: "Media file renaming and organization", keywords: ["rename", "organize", "files", "media"] },
  "automation-flaresolverr": { description: "Cloudflare challenge solver proxy", keywords: ["cloudflare", "proxy", "captcha", "bypass"] },
  "automation-flexget": { description: "Multipurpose automation tool", keywords: ["automation", "rss", "download", "schedule"] },
  "automation-headphones": { description: "Music download automation", keywords: ["music", "download", "auto", "albums"] },
  "automation-notifiarr": { description: "Discord notification integration", keywords: ["notifications", "discord", "alerts", "webhooks"] },
  "automation-seedcross": { description: "Cross-seed torrent automation", keywords: ["cross-seed", "torrent", "ratio", "seed"] },
  "automation-unpackerr": { description: "Automated archive extraction", keywords: ["extract", "unpack", "rar", "zip", "archives"] },
  "file-management-btsync": { description: "Peer-to-peer file synchronization", keywords: ["sync", "p2p", "files", "backup"] },
  "file-management-duplicati": { description: "Encrypted backup solution", keywords: ["backup", "encrypted", "cloud", "restore"] },
  "file-management-filebrowser": { description: "Web-based file manager", keywords: ["files", "browse", "manager", "upload", "download"] },
  "file-management-nextcloud": { description: "Self-hosted cloud storage and collaboration", keywords: ["cloud", "storage", "files", "sync", "share"] },
  "file-management-rclone": { description: "Cloud storage sync and management", keywords: ["cloud", "sync", "mount", "storage", "s3"] },
  "file-management-syncthing": { description: "Continuous file synchronization", keywords: ["sync", "files", "p2p", "continuous"] },
  "readers-calibre": { description: "Ebook management and conversion", keywords: ["ebooks", "books", "calibre", "convert", "library"] },
  "readers-kavita": { description: "Self-hosted digital library", keywords: ["manga", "comics", "books", "reading", "library"] },
  "readers-komga": { description: "Comic and manga server", keywords: ["comics", "manga", "reading", "server"] },
  "remote-access-novnc": { description: "Browser-based VNC remote desktop", keywords: ["vnc", "remote", "desktop", "browser"] },
  "remote-access-webconsole": { description: "Web-based terminal access", keywords: ["terminal", "console", "shell", "ssh"] },
  "remote-access-x2go": { description: "Remote desktop access", keywords: ["remote", "desktop", "gui", "x11"] },
  "communication-quassel": { description: "IRC client with detachable core", keywords: ["irc", "chat", "messaging"] },
  "communication-thelounge": { description: "Self-hosted web IRC client", keywords: ["irc", "chat", "web", "messaging"] },
  "communication-znc": { description: "IRC bouncer and proxy", keywords: ["irc", "bouncer", "proxy", "persistent"] },
  "utilities-fail2ban": { description: "Intrusion prevention and log monitoring", keywords: ["security", "ban", "brute-force", "intrusion", "jail"] },
  "utilities-lecert": { description: "SSL certificate management via Let's Encrypt", keywords: ["ssl", "tls", "certificate", "https", "letsencrypt"] },
  "utilities-netdata": { description: "Real-time system monitoring dashboard", keywords: ["monitoring", "charts", "performance", "metrics", "realtime"] },
  "utilities-phpmyadmin": { description: "Web-based MySQL/MariaDB administration", keywords: ["database", "mysql", "mariadb", "sql", "admin"] },
  "utilities-vpn": { description: "VPN service management", keywords: ["vpn", "tunnel", "network", "remote"] },
  "utilities-wireguard": { description: "Modern VPN using WireGuard protocol", keywords: ["vpn", "wireguard", "tunnel", "network"] },

  // ── Deep Pages (routes that exist but aren't in sidebar navConfig) ──
  "content-media-library": {
    description: "Manage uploaded media assets and content files",
    keywords: ["media", "library", "uploads", "assets", "content", "files", "images"]
  },
  "users-registration": {
    description: "User registration management and pending invitations",
    keywords: ["registration", "signup", "invite", "pending", "new users", "onboarding"]
  }
};
